const LOG_ERROR_PREFIXES = ['UN.js_err', 'UN_Error_', 'UN_Stale_Resource_', 'UN_UI', 'UN_MULTI_TAB_'];

const $ = global.$;
const es = global.es;

type CommonErrorDetails = {
    code: string,
    message: string,
    exceptionMessage: string,
    emailMessage: string,
    emailExceptionMessage: string
};

export type ErrorDetails = CommonErrorDetails & {
    type: "JS_ERROR" | "SERVER_ERROR",
    severity: "NOTICE" | "WARN" | "UI_ERROR" | "ERROR",
    details?: string,
    explainedError?: string,
    isHtmlMsg?: boolean
};

export type SFDCErrorDetails = CommonErrorDetails & {
    isInvalidSessionId: boolean,
    explainedError: string
}

export type ErrorHandler = {
    displayError: (err: ErrorDetails) => void,
    displaySfdcError: (err: SFDCErrorDetails) => void,
    displayUIErrorMessage: (errMsg: string) => void
}

export default class ErrorControl {
    private firstJsErrorCaught: boolean;

    ignoreErrors: boolean;
    mailtoClicked: boolean;

    private errorHandler: ErrorHandler;

    constructor() {
        this.firstJsErrorCaught = false;
        this.ignoreErrors = false;
        this.mailtoClicked = false;

        this.errorHandler = new DefaultErrorHandler();

        this.addListeners();
    }

    setErrorHandler(handler: ErrorHandler) {
        this.errorHandler = handler;
    }

    /**
     * 
     */
    private addListeners() {
        window.addEventListener('error', (e) => this.errHandler(e));

        window.addEventListener('unhandledrejection', event => {
            // Firefox raises unhandledrejection with reason === null for jQuery deferreds / XHRs
            // aborted by page navigation. There's nothing to log and no user impact.
            if (this.ignoreErrors || event.reason == null) {
                // Suppress the browser's default "Uncaught (in promise)" console message
                event.preventDefault();
                es.log.log("Unhandled promise rejection during page navigation", event.reason, event);
                return;
            }

            es.log.error("Unhandled promise rejection", event.reason, event);

            this.errHandler(event.reason?.message + " : " + event.reason?.stack);
        });
        
        // --- handle logging clientside errors to server BEGIN ---
        // Add listener for native js errors
        const throttledErrorLogListener = _.throttle((data) => {
            if (data[0] && LOG_ERROR_PREFIXES.some(prefix => data[0].startsWith(prefix))) {
                es.api.logging.clientsideErr(JSON.stringify(data[0]));
            }
        }, 1000, {leading: true, trailing: true});
        
        es.util.TrackingEvents.subscribe(throttledErrorLogListener);
        // --- handle logging clientside errors to server END ---
        
        // override/redefine the exceptionHook now that we have more error handling options
        // deferred.js -> TrackingEvents.js -> ErrorControl.js -> api.js -> marketing.js
        $.Deferred.exceptionHook = (e, _t) => {
            const W = /^(Eval|Internal|Range|Reference|Syntax|Type|URI)Error$/;
            if (W.test(e?.name)) {
                console?.error("Exception.Inside.Jquery.Deferred: ", e, _t);
                this.errHandler('UN.js_err : Exception.Inside.Jquery.Deferred: ' + e?.stack);
            }
        }
        
        $(window).on('beforeunload', () => {
            if (this.mailtoClicked) {
                this.mailtoClicked = false;
            } else {
                this.ignoreErrors = true;
            }
        });
    }

    /**
     * 
     * @param e 
     */
    private errHandler(e: string | ErrorEvent) {
        let sFormattedMessage = "";
        if (typeof e === "string") {
            sFormattedMessage = e;
        } else {
            // Get the error properties from the error event object
            const { message, filename, lineno, colno, error } = e;
            sFormattedMessage = '[' + filename + ' (' + lineno + ":" + colno + ')] ' + message + " : " + (error?.stack);
        }
        if(sFormattedMessage.includes("[ (0:0)] Script error. : undefined")){
            // skip. we've no idea what to do with these, think they are always from other libs e.g. google analytics:
            // cb=gapi.loaded_0?le=…m_migration_mod:115 Uncaught [object Object]
            return;
        }
        es.util.TrackingEvents.push(['UN.js_err : ' + sFormattedMessage]);

        if (!this.firstJsErrorCaught) {
            // if there's a js err then attempt to rerun i18n on page after 3 seconds.
            // only do this once we don't want it running on mousecrolll or pan or anything frequent.
            this.firstJsErrorCaught = true;

            window.setTimeout(() => {
                try {
                    $.i18n.translate(document);
                    if(es?.isDeveloper){
                        console.log("Reran $i18n on document");
                    }
                } catch (er){
                    console.error("Error trying to rerun i18n on page");
                }
            }, 3000);
        }
    }

    /**
     * Shows error message.
     * DOESN'T ESCAPE CHARS (i.e. message can contain HTML, so beware of XSS injection)
     *
     * @param err can be an object with responseText property with the error message, or a String
     */
    handleUIError(err: string | {responseText: string}) {
        const errMsg = err.responseText || err;

        es.util.TrackingEvents.push(["UN_UI_ERROR_" + errMsg]);

        this.errorHandler.displayUIErrorMessage(errMsg);
    }

    /**
     * 
     * @param jqXHR 
     * 
     * @returns 
     */
    handleSfdcServerErrorWithXHR(jqXHR: JQuery.jqXHR) {
        if (this.ignoreErrors){
            return;
        }

        const error = this.getNormalizedSfdcServerErrorWithXHR(jqXHR);
        this.displaySfdcError(error);
    }

    /**
     * 
     * @param sfResponse 
     * @param url 
     * @param method 
     * 
     * @returns 
     */
    handleSfdcError(sfResponse, url: string, method) {
        if (this.ignoreErrors) {
            return;
        }

        const error = this.getNormalizedSfdcErrorWithResponse(sfResponse, url, method);
        this.displaySfdcError(error);
    }

    /**
     * Display err msg
     * @param err exception obj or error string
     * @param msg optional extra error msg to display
     */
    handleJSError(err, msg) {
        if (this.ignoreErrors){
            return;
        }

        const error = this.getNormalizedJSError(err, msg);
        this.displayError(error);
    }


    private getNormalizedSfdcServerErrorWithXHR(jqXHR: JQuery.jqXHR): SFDCErrorDetails {
        return this.getNormalizedSfdcError(jqXHR.status + "/" + jqXHR.statusText, jqXHR.responseText);
    }

    private getNormalizedSfdcErrorWithResponse(sfResponse, url: string, method): SFDCErrorDetails {
        let code = sfResponse.status + "/" + sfResponse.statusText;
        let message = "An error occurred performing a Salesforce action: " + method + " " + url;

        try {
            if (sfResponse["payload"] && sfResponse.payload.length > 0) {
                code = sfResponse.payload[0].errorCode + " - " + sfResponse.status + "/" + sfResponse.statusText;
                message = "An error occurred performing a Salesforce action: " + sfResponse.payload[0].message;
            }
        } catch(ex) {
            console.error("Could not parse error from sfdc client", sfResponse, url, method);
        }
        code = _.trimEnd(code, "/");
        return this.getNormalizedSfdcError(code, message);
    }

    private getNormalizedSfdcError(code: string, message: string): SFDCErrorDetails {
        const error: SFDCErrorDetails = {
            code: code,
            message: message
        };

        error.isInvalidSessionId = (error.code != null && error.code.includes("INVALID_SESSION_ID") || error.message.includes("Error authenticating with Salesforce.com"));

        if (error.isInvalidSessionId) {
            error.explainedError = es.util.HTML.encode(error.message);
        } else {
            error.explainedError = (es.util.HTML.encode(error.message) +
                (error.code ? ("<h6><br> (Error Code: "+ es.util.HTML.encode(error.code.substring(0, error.code.indexOf(' - '))) +")</h6>") : ""));
        }

        this.setCommonErrorDetails(error);

        return error;
    }

    private setCommonErrorDetails(error: CommonErrorDetails) {
        error.message = error.message ? error.message.replace("'", "\'") : "";
        error.emailMessage = error.message ? error.message.replace("'", "\'") : "";
        error.exceptionMessage = error.exceptionMessage ?  error.exceptionMessage.replace("'", "\'") : "";
        error.emailExceptionMessage = error.exceptionMessage ?  error.exceptionMessage.replace("'", " ") : "";
    }

    /**
     * 
     * @param err 
     * @param msg 
     * 
     * @returns 
     */
    private getNormalizedJSError(err: Error | string, msg?: string): ErrorDetails {
        const error: ErrorDetails = {
            type: "JS_ERROR",
            severity: "ERROR",
            message: (typeof err == "string") ? err : err.message,
            isHtmlMsg: !!(msg)
        };

        if (msg) {
            error.message = msg + "<br/><br/>Error: " + error.message;
        }

        error.details = this.getJSErrorDetails(err, error.message);
        this.setCommonErrorDetails(error);

        return error;
    }

    private getJSErrorDetails(err: Error | string, errorMessage: string): string {
        const details = [];
        for (let n in navigator) {
            const type = typeof navigator[n];
            if ((type != "object") && (type != "function")) {
                details.push(n + ": " + navigator[n]);
            }
        }
        details.push("");

        if (err.stack) { // Chrome / Firefox (Apparently IE10 will have this too - it only took 10 versions and 15 years to get that one right! ;)
            details.push(err.stack);
        } else { // try and build some kind of stack trace for cases where it is not available
            details.push("Error " + errorMessage);
        }

        return details.join("\n");
    }

    /**
     * 
     * @param jqXHR 
     */
    handleServerErrorWithXHR(jqXHR: JQueryXHR) {
        let msg = jqXHR.responseText;
        try {
            msg = JSON.parse(msg);
        } catch(e){
            // TODO: rs is not json. some errs coming back as json, some not - need to log and fix these
        }

        this.handleServerError(jqXHR.status + "/" + jqXHR.statusText, msg, jqXHR.getResponseHeader("ES_ERROR_CODE"));
    }

    /**
     * 
     * @param status 
     * @param err 
     * @param errCode 
     * 
     * @returns 
     */
    handleServerError(status, err, errCode) {
        if (this.ignoreErrors) {
            return;
        }

        const error = this.getNormalizedServerError(status, err, errCode);

        es.util.TrackingEvents.push(['UN_SF-ERR_' + (error.message || error.details || 'UNKNOWN ERROR')]);

        this.displayError(error);
    }

    getNormalizedServerError(status, err, errCode): ErrorDetails {
        const error: ErrorDetails = {
            type: "SERVER_ERROR",
            code: errCode,
            severity: err && err['severity'] || "ERROR"
        };

        const [message, details] = this.getServerErrorMessageDetails(status, err, errCode);
        error.message = message;
        error.details = details;

        const isTerritoryComplexException = (err) => err.message && err.message.includes("Territory") && err.message.includes("complex");
        const isMaxTerritoryException = (err) => err.message && err.message.includes("Max number of territories");

        error.code = isMaxTerritoryException(error) ? "" : error.code;

        if (isTerritoryComplexException(error)) {
            error.explainedError = es.util.HTML.encode(error.message) + "<br />" + $i18n("_territories_Complex_Explanation_gap") + " <a href='https://help.espatial.com/complex-territories' target='_blank' >"+ $i18n("here") +"</a> " +  $i18n("_territories_Complex_Explanation");
        } else {
            error.explainedError = error.isHtmlMsg ? error.message : es.util.HTML.encode(error.message);
            if(error.code){
                error.explainedError = error.explainedError + " (Error Code: "+ es.util.HTML.encode(error.code) + ")";
            }
        }

        this.setCommonErrorDetails(error);

        return error;
    }

    private getServerErrorMessageDetails(status, err, errCode): [string, string] {
        let message: string;
        let details: string;

        if (err != null && typeof err == "object") {
            if (err["error"]) {
                message = err.error;
                details = status + " : " + err.error;
            } else if (err["code"]) {
                message = $i18n(err.code);
                // below block may change
                const d = [err.exceptionMessage];
                if (err.exceptionVars) {
                    d.push("");
                    d.push(err.exceptionVars);
                }
                if (err.stackTrace) {
                    d.push("");
                    d.push(err.stackTrace);
                }
                details = d.join("\n");
            } else if (err["exceptionMessage"]) {
                message = err.exceptionMessage;
                const d = [err.exceptionMessage];
                if (err.exceptionVars) {
                    d.push("");
                    d.push(err.exceptionVars);
                }
                if (err.stackTrace) {
                    d.push("");
                    d.push(err.stackTrace);
                }
                details = d.join("\n");
            }
        }

        if (err == null || !details) {
            message = (err ? err : status);
            details = status + (err ? "/" + err: "");
        }

        return [message, details];
    }

    private displaySfdcError(error: SFDCErrorDetails) {
        es.util.TrackingEvents.push(["UN_ERROR_" + error.message]);
        es.util.TrackingEvents.push([error], es.util.TrackingEvents.EVENTS.trackingError);

        this.errorHandler.displaySfdcError(error);

        // Also display error in console if api is in developer mode
        console.error("esAPI (developer mode) :: ErrorControl : \n", error);
    }

    private displayError(error: ErrorDetails) {
        es.util.TrackingEvents.push(['UN_SF-ERR_' + error.message]);
        es.util.TrackingEvents.push([error], es.util.TrackingEvents.EVENTS.trackingError);

        this.errorHandler.displayError(error);
    
        // Also display error in console if api is in developer mode
        console.error("esAPI (developer mode) :: ErrorControl : \n", error);
    }
}


export class DefaultErrorHandler implements ErrorHandler {
    displayError(err: ErrorDetails) {
        this.display(err.message)
    }

    displaySfdcError(err: SFDCErrorDetails) {
        this.display(err.message);
    }

    displayUIErrorMessage(errMsg: string) {
        this.display(errMsg);
    }

    private display(errMsg: string) {
        es.alert.alertOk($i18n("_Opps_something_wrong"), errMsg);
    }
}
